import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    Button,
    Grid,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    useTheme,
    useMediaQuery,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Alert,
    Snackbar,
    CardContent
} from '@mui/material';
import {
    Add,
    Edit,
    Delete,
    Print,
    Bluetooth,
    Refresh,
    Visibility,
    Usb as UsbIcon,
} from '@mui/icons-material';
import CommonModal from '../components/common/CommonModal';
import { useNotification } from '../contexts/NotificationContext';
import { get_printers,create_printer,edit_printer, } from '../services/printerService';
import axios from 'axios';

const PrinterSettingsManagement = () => {
    const { showSuccess, showError } = useNotification();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [printers, setPrinters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState({ type: 'create', value: false });
    const [editingPrinter, setEditingPrinter] = useState(null);
    const [viewPrinterId, setViewPrinterId] = useState(null);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [formData, setFormData] = useState({
        printer_name: '',
        name: '',
        printer_type: '',
        pdf_type: '',
        connection_type: 'bluetooth', // bluetooth, usb, network
        mac_address: '',
        ip_address: '',
    });

    // Bluetooth discovery state
    const [bluetoothModalOpen, setBluetoothModalOpen] = useState(false);
    const [discovering, setDiscovering] = useState(false);
    const [discoveredDevices, setDiscoveredDevices] = useState([]);
    const [connecting, setConnecting] = useState(null);
    const [connectionMethod, setConnectionMethod] = useState('bluetooth'); 
    const [printerTypes,setPrinterTypes] = useState([])
    


    // const printerTypes = ['Laser', 'Inkjet', 'Thermal', 'Dot Matrix', '3D Printer', 'Label Printer'];
    const pdfTypes = ['PDF/A', 'PDF/X', 'PDF/E', 'Standard PDF', 'ZPL', 'EPL', 'ESC/POS'];

    useEffect(() => {
        fetchPrinters();
        fetchPrinterTypes();
        // Check if Web Bluetooth is supported
        checkBluetoothSupport();
    }, [modalOpen]);

    const checkBluetoothSupport = () => {
        if (navigator.bluetooth) {
            // console.log('Web Bluetooth is supported');
        } else {
            console.warn('Web Bluetooth is not supported in this browser');
            showError('Web Bluetooth is not supported. Please use Chrome, Edge, or Opera browser.');
        }
    };

    const fetchPrinterTypes = async () => {
        try{
            const response = await axios.get('http://127.1.0.1:32568/printer_list')
            // console.log("Response => ", response);
            setPrinterTypes(response.data)
        }
        catch(error){
            console.error("Error on Getting Printer Types => ", error);
        }
    }

    const fetchPrinters = async () => {
        setLoading(true);
        try {
            // Replace with your actual API call
            const response = await get_printers()
            // console.log(response.data.result)
            setPrinters(response.data.result);
        } catch (error) {
            // Mock data for demo
            // setTimeout(() => {
            //     setPrinters([
            //         { 
            //             id: '1', 
            //             printer_name: 'Office HP', 
            //             name: 'HP LaserJet Pro', 
            //             printer_type: 'Laser', 
            //             pdf_type: 'PDF/A',
            //             connection_type: 'network',
            //             ip_address: '192.168.1.100',
            //             created_at: '2024-01-15T10:30:00Z',
            //             status: 'active'
            //         },
            //         { 
            //             id: '2', 
            //             printer_name: 'Kitchen Thermal', 
            //             name: 'Epson TM-T88V', 
            //             printer_type: 'Thermal', 
            //             pdf_type: 'ESC/POS',
            //             connection_type: 'bluetooth',
            //             mac_address: '00:11:22:33:44:55',
            //             created_at: '2024-02-20T14:45:00Z',
            //             status: 'active'
            //         },
            //     ]);
            //     setLoading(false);
            // }, 500);
        } finally {
            setLoading(false);
        }
    };

    // Real Bluetooth Discovery for Thermal Printers
    const discoverBluetoothPrinters = async () => {
        if (!navigator.bluetooth) {
            showError('Web Bluetooth is not supported in this browser. Please use Chrome, Edge, or Opera.');
            return [];
        }

        try {
            // Request device with specific filters for printers
            const device = await navigator.bluetooth.requestDevice({
                filters: [
                    // Common printer service UUIDs
                    { services: ['000018f0-0000-1000-8000-00805f9b34fb'] }, // Printer Service
                    { services: ['00001101-0000-1000-8000-00805f9b34fb'] }, // Serial Port Profile
                    { services: ['0000110a-0000-1000-8000-00805f9b34fb'] }, // OBEX Object Push
                    { namePrefix: 'Zebra' },
                    { namePrefix: 'Epson' },
                    { namePrefix: 'Brother' },
                    { namePrefix: 'HP' },
                    { namePrefix: 'Thermal' },
                    { namePrefix: 'Printer' },
                ],
                acceptAllDevices: false,
            });

            // Get device information
            const deviceInfo = {
                id: device.id,
                name: device.name || 'Unknown Printer',
                address: device.id,
                device: device,
                rssi: device.rssi || 'N/A',
            };

            // Try to connect and get services
            try {
                const server = await device.gatt.connect();
                deviceInfo.connected = true;
                
                // Get primary services
                const services = await server.getPrimaryServices();
                deviceInfo.services = services.map(s => s.uuid);
                
                // Disconnect after getting info
                await server.disconnect();
            } catch (connError) {
                console.warn('Could not connect to get services:', connError);
                deviceInfo.connected = false;
            }

            return [deviceInfo];
        } catch (error) {
            if (error.name === 'NotFoundError') {
                showError('No Bluetooth printer selected or found');
            } else if (error.name === 'SecurityError') {
                showError('Bluetooth permission denied. Please allow Bluetooth access.');
            } else {
                showError(`Bluetooth error: ${error.message}`);
            }
            return [];
        }
    };

    // Discover multiple devices (for scanners that support it)
    const discoverMultiplePrinters = async () => {
        if (!navigator.bluetooth) {
            showError('Web Bluetooth is not supported');
            return [];
        }

        try {
            const devices = await navigator.bluetooth.getDevices();
            if (devices.length === 0) {
                // If no paired devices, request new one
                return await discoverBluetoothPrinters();
            }
            
            return devices.map(device => ({
                id: device.id,
                name: device.name || 'Unknown Printer',
                address: device.id,
                device: device,
            }));
        } catch (error) {
            console.error('Error getting paired devices:', error);
            return [];
        }
    };

    const handleDiscoverBluetooth = async () => {
        setBluetoothModalOpen(true);
        setDiscovering(true);
        setDiscoveredDevices([]);
        
        try {
            let devices = [];
            
            if (connectionMethod === 'bluetooth') {
                // Try to discover new devices
                const newDevice = await discoverBluetoothPrinters();
                if (newDevice.length > 0) {
                    devices = newDevice;
                }
                
                // Also get already paired devices
                const pairedDevices = await discoverMultiplePrinters();
                devices = [...devices, ...pairedDevices];
                
                // Remove duplicates
                devices = devices.filter((device, index, self) => 
                    index === self.findIndex(d => d.id === device.id)
                );
            }
            
            setDiscoveredDevices(devices);
            
            if (devices.length === 0) {
                showError('No Bluetooth printers found. Make sure your printer is in pairing mode.');
            } else {
                showSuccess(`Found ${devices.length} Bluetooth printer(s)`);
            }
        } catch (error) {
            console.error('Discovery error:', error);
            showError(`Discovery failed: ${error.message}`);
        } finally {
            setDiscovering(false);
        }
    };

    const handleConnectBluetooth = async (device) => {
        setConnecting(device.id);
        
        try {
            if (device.device && device.device.gatt) {
                // Try to connect to the printer
                const server = await device.device.gatt.connect();
                
                // For thermal printers, try to find the printer service
                const printerService = await server.getPrimaryService('000018f0-0000-1000-8000-00805f9b34fb');
                
                if (printerService) {
                    // Found printer service
                    showSuccess(`Successfully connected to ${device.name}`);
                    
                    // Auto-fill the form with printer details
                    setFormData(prev => ({
                        ...prev,
                        printer_name: device.name,
                        name: device.name,
                        printer_type: 'Thermal',
                        connection_type: 'bluetooth',
                        mac_address: device.id,
                    }));
                    
                    setBluetoothModalOpen(false);
                    setModalOpen({ type: 'create', value: true });
                } else {
                    // Connect anyway even if specific service not found
                    showSuccess(`Connected to ${device.name}`);
                    setFormData(prev => ({
                        ...prev,
                        printer_name: device.name,
                        name: device.name,
                        connection_type: 'bluetooth',
                        mac_address: device.id,
                    }));
                    setBluetoothModalOpen(false);
                    setModalOpen({ type: 'create', value: true });
                }
            } else {
                // Manual entry for device without live connection
                showSuccess(`Device ${device.name} selected`);
                setFormData(prev => ({
                    ...prev,
                    printer_name: device.name,
                    name: device.name,
                    connection_type: 'bluetooth',
                    mac_address: device.id,
                }));
                setBluetoothModalOpen(false);
                setModalOpen({ type: 'create', value: true });
            }
        } catch (error) {
            console.error('Connection error:', error);
            showError(`Failed to connect: ${error.message}`);
            
            // Still allow manual entry
            if (window.confirm(`Could not establish connection, but would you like to add ${device.name} manually?`)) {
                setFormData(prev => ({
                    ...prev,
                    printer_name: device.name,
                    name: device.name,
                    connection_type: 'bluetooth',
                    mac_address: device.id,
                }));
                setBluetoothModalOpen(false);
                setModalOpen({ type: 'create', value: true });
            }
        } finally {
            setConnecting(null);
        }
    };

    // USB Discovery for thermal printers
    const discoverUSBPrinters = async () => {
        if (!navigator.usb) {
            showError('WebUSB is not supported in this browser');
            return [];
        }

        try {
            const device = await navigator.usb.requestDevice({
                filters: [
                    { vendorId: 0x04b8 }, // Epson
                    { vendorId: 0x0a5c }, // Zebra
                    { vendorId: 0x04f9 }, // Brother
                    { vendorId: 0x03f0 }, // HP
                    { vendorId: 0x0b05 }, // Other printers
                ]
            });
            
            await device.open();
            await device.selectConfiguration(1);
            await device.claimInterface(0);
            
            const deviceInfo = {
                id: device.serialNumber || Date.now().toString(),
                name: device.productName || 'USB Printer',
                address: device.serialNumber || 'USB Connected',
                device: device,
                vendorId: device.vendorId,
                productId: device.productId,
            };
            
            return [deviceInfo];
        } catch (error) {
            if (error.name !== 'NotFoundError') {
                showError(`USB error: ${error.message}`);
            }
            return [];
        }
    };

    const handleDiscoverUSB = async () => {
        setBluetoothModalOpen(true);
        setDiscovering(true);
        setDiscoveredDevices([]);
        setConnectionMethod('usb');
        
        try {
            const devices = await discoverUSBPrinters();
            setDiscoveredDevices(devices);
            
            if (devices.length === 0) {
                showError('No USB printers found. Make sure your printer is connected.');
            }
        } catch (error) {
            showError(`USB discovery failed: ${error.message}`);
        } finally {
            setDiscovering(false);
        }
    };

    const handleTestPrint = async (printer) => {
        try {
            showSuccess(`Sending test print to ${printer.printer_name}...`);
            
            // For thermal printers, you would send ESC/POS or ZPL commands
            // Example test data for thermal printer
            const testData = [
                '\x1B\x40', // Initialize printer
                '\x1B\x61\x01', // Center align
                'TEST PRINT\n',
                'Printer: ' + printer.name + '\n',
                'Type: ' + printer.printer_type + '\n',
                'Date: ' + new Date().toLocaleString() + '\n',
                '\n\n\n',
                '\x1D\x56\x00', // Cut paper (for thermal printers)
            ].join('');
            
            // Send to printer via appropriate connection
            if (printer.connection_type === 'bluetooth' && printer.bluetooth_device) {
                // Send via Bluetooth
                await sendBluetoothPrint(printer.bluetooth_device, testData);
            } else {
                // Mock success for demo
                setTimeout(() => {
                    showSuccess(`Test print sent to ${printer.printer_name}`);
                }, 1000);
            }
        } catch (error) {
            showError(`Test print failed: ${error.message}`);
        }
    };

    const sendBluetoothPrint = async (device, data) => {
        if (!device || !device.gatt) {
            throw new Error('Device not connected');
        }
        
        const server = await device.gatt.connect();
        const service = await server.getPrimaryService('000018f0-0000-1000-8000-00805f9b34fb');
        const characteristic = await service.getCharacteristic('00002af1-0000-1000-8000-00805f9b34fb');
        
        const encoder = new TextEncoder();
        await characteristic.writeValue(encoder.encode(data));
        
        await server.disconnect();
    };

    const handleOpenDialog = (printer) => {
        if (printer) {
            setEditingPrinter(printer);
            setFormData({
                printer_name: printer.printer_name,
                name: printer.name,
                printer_type: printer.printer_type,
                pdf_type: printer.pdf_type,
                connection_type: printer.connection_type || 'bluetooth',
                mac_address: printer.mac_address || '',
                ip_address: printer.ip_address || '',
            });
            setModalOpen({ type: 'edit', value: true });
        } else {
            setEditingPrinter(null);
            setFormData({ 
                printer_name: '', 
                name: '', 
                printer_type: '', 
                pdf_type: '',
                connection_type: 'bluetooth',
                mac_address: '',
                ip_address: '',
            });
            setModalOpen({ type: 'create', value: true });
        }
    };

    const handleCloseDialog = () => {
        setModalOpen({ type: 'create', value: false });
        setEditingPrinter(null);
        setFormData({ 
            printer_name: '', 
            name: '', 
            printer_type: '', 
            pdf_type: '',
            connection_type: 'bluetooth',
            mac_address: '',
            ip_address: '',
        });
    };

    const handleFormChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async () => {
        if (!formData.printer_name || !formData.name || !formData.printer_type) {
            showError('Please fill all required fields');
            return;
        }

        try {
            if (editingPrinter) {
                // Update API call
                const response = await edit_printer(formData)
                // setPrinters(prev => prev.map(p => p.id === editingPrinter.id ? updated : p));
                showSuccess('Printer updated successfully');
            } else {
                // Create API call
                const response = await create_printer(formData)
                // setPrinters(prev => [...prev, created]);
                showSuccess('Printer added successfully');
            }
            handleCloseDialog();
        } catch (error) {
            // Mock success for demo
            if (editingPrinter) {
                setPrinters(prev => prev.map(p => p.id === editingPrinter.id ? { ...editingPrinter, ...formData } : p));
            } else {
                const newPrinter = { 
                    ...formData, 
                    id: Date.now().toString(), 
                    created_at: new Date().toISOString(), 
                    status: 'active' 
                };
                setPrinters(prev => [...prev, newPrinter]);
            }
            showSuccess(editingPrinter ? 'Printer updated successfully' : 'Printer added successfully');
            handleCloseDialog();
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this printer?')) {
            try {
                await fetch(`/api/printers/${id}`, { method: 'DELETE' });
                setPrinters(prev => prev.filter(p => p.id !== id));
                showSuccess('Printer deleted successfully');
            } catch (error) {
                setPrinters(prev => prev.filter(p => p.id !== id));
                showSuccess('Printer deleted successfully');
            }
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <Box>
            <Box sx={{
                mb: { xs: 3, sm: 4 },
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: { xs: 2, sm: 0 }
            }}>
                <Box>
                    <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" gutterBottom>
                        Printer Settings Management
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Manage your printers and their configurations
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    {/* <Button
                        variant="outlined"
                        startIcon={<Bluetooth />}
                        onClick={handleDiscoverBluetooth}
                        sx={{ borderRadius: 2, textTransform: 'none' }}
                    >
                        Discoverable
                    </Button> */}
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => handleOpenDialog()}
                        sx={{ borderRadius: 2, textTransform: 'none' }}
                    >
                        Add Printer
                    </Button>
                </Box>
            </Box>

            {/* Printers List */}
            {printers.length === 0 && !loading ? (
                <Card sx={{ p: 3, pb: 2, textAlign: 'center', bgcolor: '#fff', boxShadow: 1 }}>
                    <CardContent>
                        <Typography variant="h6" color="text.secondary">
                            No Printers Available
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Add new printers to get started
                        </Typography>
                    </CardContent>
                </Card>
            ) : (
                <Card>
                    <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
                        <Table sx={{ minWidth: 600 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>S.No</TableCell>
                                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Printer Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Model Name</TableCell>
                                    {/* <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Printer Type</TableCell> */}
                                    {/* <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>PDF Type</TableCell>
                                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Connection</TableCell> */}
                                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Added On</TableCell>
                                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                            <CircularProgress />
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    printers.map((printer, index) => (
                                        <TableRow key={printer.id} hover>
                                            <TableCell>{index + 1}</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Print sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                    <Typography variant="body2">{printer.printer_name}</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>{printer.name}</TableCell>
                                            {/* <TableCell>
                                                <Chip label={printer.printer_type} size="small" color="primary" variant="outlined" />
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={printer.pdf_type} size="small" color="secondary" variant="outlined" />
                                            </TableCell> */}
                                            {/* <TableCell>
                                                <Chip 
                                                    label={printer.connection_type || 'Bluetooth'} 
                                                    size="small" 
                                                    icon={printer.connection_type === 'usb' ? <UsbIcon /> : <Bluetooth />}
                                                    sx={{ textTransform: 'capitalize' }}
                                                />
                                            </TableCell> */}
                                            <TableCell>{formatDate(printer.created_at)}</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    {/* <IconButton size="small" onClick={() => handleTestPrint(printer)} title="Test Print">
                                                        <Print sx={{ fontSize: 20 }} />
                                                    </IconButton> */}
                                                    <IconButton size="small" onClick={() => handleOpenDialog(printer)}>
                                                        <Edit sx={{ fontSize: 20 }} />
                                                    </IconButton>
                                                    <IconButton size="small" color="error" onClick={() => handleDelete(printer.id)}>
                                                        <Delete sx={{ fontSize: 20 }} />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            )}

            {/* Add/Edit Printer Modal */}
            <Dialog open={modalOpen.value} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Typography variant="h6" fontWeight="bold">
                        {modalOpen.type === 'create' ? 'Add New Printer' : 'Edit Printer'}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Printer Name"
                                value={formData.printer_name}
                                onChange={(e) => handleFormChange('printer_name', e.target.value)}
                                placeholder="e.g., Kitchen Thermal Printer"
                                required
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Model Name"
                                value={formData.name}
                                onChange={(e) => handleFormChange('name', e.target.value)}
                                placeholder="e.g., Epson TM-T88V"
                                required
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth required size="small">
                                <InputLabel>Printer Type</InputLabel>
                                <Select
                                    value={formData.printer_type}
                                    onChange={(e) => handleFormChange('printer_type', e.target.value)}
                                    label="Printer Type"
                                >
                                    {printerTypes.map((type) => (
                                        <MenuItem key={type.name} value={type.name}>{type.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        {/* <Grid item xs={12}>
                            <FormControl fullWidth required size="small">
                                <InputLabel>PDF Type / Printer Language</InputLabel>
                                <Select
                                    value={formData.pdf_type}
                                    onChange={(e) => handleFormChange('pdf_type', e.target.value)}
                                    label="PDF Type / Printer Language"
                                >
                                    {pdfTypes.map((type) => (
                                        <MenuItem key={type} value={type}>{type}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Connection Type</InputLabel>
                                <Select
                                    value={formData.connection_type}
                                    onChange={(e) => handleFormChange('connection_type', e.target.value)}
                                    label="Connection Type"
                                >
                                    <MenuItem value="bluetooth">Bluetooth</MenuItem>
                                    <MenuItem value="usb">USB</MenuItem>
                                    <MenuItem value="network">Network (WiFi/Ethernet)</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        {formData.connection_type === 'bluetooth' && (
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="MAC Address / Device ID"
                                    value={formData.mac_address}
                                    onChange={(e) => handleFormChange('mac_address', e.target.value)}
                                    placeholder="00:11:22:33:44:55"
                                    size="small"
                                    helperText="Click 'Discoverable' button to find Bluetooth printers"
                                />
                            </Grid>
                        )}
                        {formData.connection_type === 'network' && (
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="IP Address"
                                    value={formData.ip_address}
                                    onChange={(e) => handleFormChange('ip_address', e.target.value)}
                                    placeholder="192.168.1.100"
                                    size="small"
                                />
                            </Grid>
                        )} */}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button onClick={handleCloseDialog} variant="outlined">Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained">
                        {modalOpen.type === 'create' ? 'Submit' : 'Update'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Bluetooth Discovery Modal */}
            <Dialog open={bluetoothModalOpen} onClose={() => setBluetoothModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Bluetooth color="primary" />
                        <Typography variant="h6" fontWeight="bold">
                            Discover Printers
                        </Typography>
                        <IconButton 
                            size="small" 
                            onClick={handleDiscoverBluetooth} 
                            disabled={discovering}
                            sx={{ ml: 'auto' }}
                        >
                            <Refresh />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                        <Button 
                            variant={connectionMethod === 'bluetooth' ? 'contained' : 'outlined'}
                            size="small"
                            onClick={() => {
                                setConnectionMethod('bluetooth');
                                handleDiscoverBluetooth();
                            }}
                            startIcon={<Bluetooth />}
                        >
                            Bluetooth
                        </Button>
                        <Button 
                            variant={connectionMethod === 'usb' ? 'contained' : 'outlined'}
                            size="small"
                            onClick={handleDiscoverUSB}
                            startIcon={<UsbIcon />}
                        >
                            USB
                        </Button>
                    </Box>
                    
                    {discovering ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                            <CircularProgress />
                            <Typography sx={{ mt: 2 }}>Scanning for printers...</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                                Make sure your printer is in pairing/discovery mode
                            </Typography>
                        </Box>
                    ) : discoveredDevices.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Print sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                            <Typography color="text.secondary">No printers found</Typography>
                            <Typography variant="caption" color="text.secondary">
                                {connectionMethod === 'bluetooth' 
                                    ? 'Make sure Bluetooth is enabled and printer is in pairing mode'
                                    : 'Make sure printer is connected via USB'}
                            </Typography>
                            <Button size="small" onClick={connectionMethod === 'bluetooth' ? handleDiscoverBluetooth : handleDiscoverUSB} sx={{ mt: 2 }}>
                                Scan Again
                            </Button>
                        </Box>
                    ) : (
                        <Grid container spacing={2}>
                            {discoveredDevices.map((device) => (
                                <Grid item xs={12} key={device.id}>
                                    <Paper sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box>
                                            <Typography variant="subtitle2" fontWeight="bold">{device.name}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                ID: {device.address}
                                                {device.vendorId && ` | Vendor: ${device.vendorId}`}
                                            </Typography>
                                        </Box>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={() => handleConnectBluetooth(device)}
                                            disabled={connecting === device.id}
                                            startIcon={connecting === device.id ? <CircularProgress size={16} /> : <Bluetooth />}
                                        >
                                            {connecting === device.id ? 'Connecting...' : 'Connect'}
                                        </Button>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBluetoothModalOpen(false)}>Close</Button>
                    <Button onClick={() => {
                        setBluetoothModalOpen(false);
                        setModalOpen({ type: 'create', value: true });
                    }}>
                        Add Manually
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View Printer Details Modal */}
            <CommonModal
                modalOpen={viewModalOpen}
                setModalOpen={setViewModalOpen}
                children={<ViewPrinterDetails printerId={viewPrinterId} />}
            />
        </Box>
    );
};

// View Printer Details Component
const ViewPrinterDetails = ({ printerId }) => {
    const [details, setDetails] = useState(null);
    const { showError } = useNotification();

    useEffect(() => {
        async function fetchPrinterDetail() {
            if (!printerId) return;
            try {
                const response = await fetch(`/api/printers/${printerId}`);
                const data = await response.json();
                setDetails(data);
            } catch (error) {
                // Mock data
                setDetails({
                    printer_name: 'Kitchen Thermal Printer',
                    name: 'Epson TM-T88V',
                    printer_type: 'Thermal',
                    pdf_type: 'ESC/POS',
                    connection_type: 'bluetooth',
                    mac_address: '00:11:22:33:44:55',
                    created_at: new Date().toISOString(),
                });
            }
        }
        fetchPrinterDetail();
    }, [printerId]);

    return (
        <Box sx={{ p: 3, maxWidth: 400, margin: '0 auto' }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main', textAlign: 'center', mb: 3, pb: 2, borderBottom: '2px solid', borderColor: 'divider' }}>
                Printer Details
            </Typography>
            <Box sx={{ backgroundColor: 'grey.50', borderRadius: 2, p: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <Box sx={{ mb: 2.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                        Printer Name
                    </Typography>
                    <Typography sx={{ fontWeight: 500, fontSize: '1.1rem' }}>{details?.printer_name || 'Not provided'}</Typography>
                </Box>
                <Box sx={{ mb: 2.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                        Model Name
                    </Typography>
                    <Typography sx={{ fontWeight: 500 }}>{details?.name || 'Not provided'}</Typography>
                </Box>
                <Box sx={{ mb: 2.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                        Printer Type
                    </Typography>
                    <Typography sx={{ fontWeight: 500 }}>{details?.printer_type || 'Not provided'}</Typography>
                </Box>
                <Box sx={{ mb: 2.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                        PDF Type / Language
                    </Typography>
                    <Typography sx={{ fontWeight: 500 }}>{details?.pdf_type || 'Not provided'}</Typography>
                </Box>
                <Box sx={{ mb: 2.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                        Connection Type
                    </Typography>
                    <Typography sx={{ fontWeight: 500, textTransform: 'capitalize' }}>{details?.connection_type || 'Not provided'}</Typography>
                </Box>
                {details?.mac_address && (
                    <Box sx={{ mb: 2.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                            MAC Address
                        </Typography>
                        <Typography sx={{ fontWeight: 500 }}>{details.mac_address}</Typography>
                    </Box>
                )}
                <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                        Added On
                    </Typography>
                    <Typography sx={{ fontWeight: 500 }}>
                        {details?.created_at ? new Date(details.created_at).toLocaleDateString() : 'Not available'}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

export default PrinterSettingsManagement;